import { useMemo } from "react";
import { Globe } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Curated list of high-traffic timezones. Browsers also expose
// Intl.supportedValuesOf('timeZone') with the full IANA list - we merge them.
const COMMON_TZ = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Moscow",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
];

function useTimezones() {
  return useMemo(() => {
    let all = COMMON_TZ;
    try {
      // eslint-disable-next-line no-undef
      if (typeof Intl?.supportedValuesOf === "function") {
        const full = Intl.supportedValuesOf("timeZone") || [];
        const set = new Set([...COMMON_TZ, ...full]);
        all = Array.from(set).sort();
      }
    } catch {
      // older browsers - stay with curated list
    }
    return all;
  }, []);
}

function browserTz() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * Controlled editor for { timezone, sending_window: { start_hour, end_hour, days[] } }.
 *
 * Props:
 *  - timezone: string (IANA)
 *  - sendingWindow: { start_hour, end_hour, days[] }
 *  - onChange: ({ timezone?, sending_window? }) => void
 *  - testIdPrefix: string (optional)
 */
export default function SendingWindowEditor({
  timezone,
  sendingWindow,
  onChange,
  testIdPrefix = "sw",
}) {
  const tzs = useTimezones();
  const tz = timezone || "UTC";
  const w = sendingWindow || { start_hour: 0, end_hour: 24, days: [0, 1, 2, 3, 4, 5, 6] };
  const isAllDay = w.start_hour === 0 && w.end_hour === 24;
  const isAllWeek = (w.days || []).length === 7;
  const is247 = isAllDay && isAllWeek;

  const update = (patch) => onChange({ ...patch });
  const updateWindow = (patch) =>
    onChange({ sending_window: { ...w, ...patch } });
  const toggleDay = (idx) => {
    const days = w.days || [];
    const next = days.includes(idx)
      ? days.filter((d) => d !== idx)
      : [...days, idx].sort();
    updateWindow({ days: next });
  };
  const setPreset = (preset) => {
    if (preset === "24_7") {
      update({
        sending_window: { start_hour: 0, end_hour: 24, days: [0, 1, 2, 3, 4, 5, 6] },
      });
    } else if (preset === "business") {
      update({
        sending_window: { start_hour: 9, end_hour: 17, days: [0, 1, 2, 3, 4] },
      });
    } else if (preset === "extended") {
      update({
        sending_window: { start_hour: 8, end_hour: 20, days: [0, 1, 2, 3, 4] },
      });
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-mono flex items-center gap-1.5">
          <Globe className="w-3 h-3" strokeWidth={1.5} /> Timezone
        </div>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <select
            value={tz}
            onChange={(e) => update({ timezone: e.target.value })}
            data-testid={`${testIdPrefix}-timezone`}
            className="bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2 text-sm font-mono min-w-[260px]"
          >
            {tzs.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => update({ timezone: browserTz() })}
            data-testid={`${testIdPrefix}-use-browser-tz`}
            className="text-[10px] font-mono uppercase tracking-[0.18em] text-neutral-400 hover:text-white border border-[#27272a] rounded-md px-2 py-1.5"
          >
            Use my timezone
          </button>
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-mono">
          Preset
        </div>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <PresetBtn
            label="24 / 7"
            active={is247}
            onClick={() => setPreset("24_7")}
            testId={`${testIdPrefix}-preset-24-7`}
          />
          <PresetBtn
            label="Business hrs (9–17, Mon–Fri)"
            active={
              w.start_hour === 9 &&
              w.end_hour === 17 &&
              JSON.stringify(w.days) === JSON.stringify([0, 1, 2, 3, 4])
            }
            onClick={() => setPreset("business")}
            testId={`${testIdPrefix}-preset-business`}
          />
          <PresetBtn
            label="Extended (8–20, Mon–Fri)"
            active={
              w.start_hour === 8 &&
              w.end_hour === 20 &&
              JSON.stringify(w.days) === JSON.stringify([0, 1, 2, 3, 4])
            }
            onClick={() => setPreset("extended")}
            testId={`${testIdPrefix}-preset-extended`}
          />
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-mono">
          Hours ({tz})
        </div>
        <div className="mt-2 flex items-center gap-2 font-mono text-sm">
          <input
            type="number"
            min={0}
            max={23}
            value={w.start_hour}
            onChange={(e) =>
              updateWindow({ start_hour: Math.max(0, Math.min(23, parseInt(e.target.value || 0))) })
            }
            data-testid={`${testIdPrefix}-start-hour`}
            className="bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2 w-20"
          />
          <span className="text-neutral-500">to</span>
          <input
            type="number"
            min={1}
            max={24}
            value={w.end_hour}
            onChange={(e) =>
              updateWindow({ end_hour: Math.max(1, Math.min(24, parseInt(e.target.value || 0))) })
            }
            data-testid={`${testIdPrefix}-end-hour`}
            className="bg-[#0d0d0d] border border-[#27272a] rounded-md px-3 py-2 w-20"
          />
          <span className="text-neutral-500 text-xs">(24 = end of day)</span>
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-mono">
          Days
        </div>
        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
          {DAYS.map((d, i) => {
            const on = (w.days || []).includes(i);
            return (
              <button
                key={d}
                type="button"
                onClick={() => toggleDay(i)}
                data-testid={`${testIdPrefix}-day-${d.toLowerCase()}`}
                className={`px-2.5 py-1 rounded-md text-xs font-mono uppercase tracking-[0.15em] border transition-colors ${
                  on
                    ? "bg-white text-black border-white"
                    : "bg-transparent text-neutral-400 border-[#27272a] hover:bg-[#161616]"
                }`}
              >
                {d}
              </button>
            );
          })}
        </div>
      </div>

      {is247 && (
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-400/80">
          ✓ 24/7 sending - no time restriction
        </div>
      )}
    </div>
  );
}

function PresetBtn({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-[0.16em] border transition-colors ${
        active
          ? "bg-white text-black border-white"
          : "bg-[#0d0d0d] text-neutral-300 border-[#27272a] hover:bg-[#161616]"
      }`}
    >
      {label}
    </button>
  );
}
